import { menuItems } from '@/shared/constants';
import { memo, useCallback, useRef, useState } from 'react';
import { SidebarMenu } from './sidebar-menu.component';
import { SidebarSettingsLink } from './sidebar-settings-link.component';
import './sidebar.styles.css';

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar = memo(function Sidebar({
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const leaveTimeoutRef = useRef<number | undefined>(undefined);
  const isNavigatingRef = useRef(false);
  const isMouseOverRef = useRef(false);

  const handleMouseEnter = useCallback(() => {
    // Clear any pending leave timeout
    if (leaveTimeoutRef.current !== undefined) {
      window.clearTimeout(leaveTimeoutRef.current);
    }
    isMouseOverRef.current = true;
    isNavigatingRef.current = false;
    setIsExpanded(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    isMouseOverRef.current = false;

    // Don't collapse if we're currently navigating
    if (isNavigatingRef.current) {
      return;
    }

    // Delay collapsing to prevent accidental collapse when clicking
    leaveTimeoutRef.current = window.setTimeout(() => {
      setIsExpanded(false);
    }, 100);
  }, []);

  const handleMenuClick = useCallback(() => {
    if (mobileOpen) {
      onMobileClose?.();
      return;
    }

    // Mark that we're navigating to prevent collapse
    isNavigatingRef.current = true;

    // Reset navigation flag and collapse if mouse is not over after delay
    setTimeout(() => {
      isNavigatingRef.current = false;

      // If mouse is not over the sidebar, collapse it
      if (!isMouseOverRef.current) {
        setIsExpanded(false);
      }
    }, 500);
  }, [mobileOpen, onMobileClose]);

  const effectiveExpanded = mobileOpen || isExpanded;

  return (
    <>
      {/* Spacer */}
      <div className='hidden h-full w-16 md:block' />

      {mobileOpen && (
        <button
          type='button'
          aria-label='Fechar menu lateral'
          className='fixed inset-0 top-[var(--app-header-bottom,64px)] z-30 bg-alba-black-500/30 md:hidden'
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar - Hover to Expand */}
      <aside
        className={`group fixed bottom-0 left-0 top-[var(--app-header-bottom,64px)] z-40 flex w-72 flex-col overflow-hidden border-r-2 border-alba-green-500 bg-alba-blue-600 py-4 shadow-lg transition-all duration-300 ease-in-out md:w-16 md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${
          effectiveExpanded ? 'md:w-72' : 'md:w-16'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Main Menu Content */}
        <div className='flex-1 overflow-y-auto overflow-x-hidden px-3 space-y-1'>
          <SidebarMenu
            items={menuItems}
            isExpanded={effectiveExpanded}
            onMenuClick={handleMenuClick}
          />
        </div>

        {/* Footer with Settings */}
        <div className='px-3 mt-4 pt-4 border-t border-alba-blue-700/50'>
          <SidebarSettingsLink
            isExpanded={effectiveExpanded}
            onMenuClick={handleMenuClick}
          />
        </div>
      </aside>
    </>
  );
});
